import { assertRequiredColumns } from '../assertRequiredColumns';
import { chartPresentationProps } from '../presentation';
import type {
  HeatmapAdapterConfig,
  HeatmapAdapterProps,
  HeatmapDataPoint,
  HeatmapLevel,
  HeatmapLook,
  HeatmapPresentation,
  RenderAdapterResult,
} from '../types';
import { validateRowsInput } from '../validateRowsInput';

const DEFAULT_LEVEL: HeatmapLevel = 'us-states';

/** Two-digit state prefix, padded the way hen-ui reads county features. */
function statePrefixFromGeoId(geoId: string): string {
  return geoId.trim().slice(0, 2).padStart(2, '0');
}

/** County FIPS are five digits; padding keeps `"6037"` aligned with `"06037"`. */
function normalizeGeoId(raw: string, level: HeatmapLevel): string {
  const trimmed = raw.trim();
  if (level !== 'counties' || !/^\d+$/.test(trimmed)) {
    return trimmed;
  }
  return trimmed.padStart(5, '0');
}

/**
 * Explicit null stays null so the map renders its muted no-data fill; coercing
 * to 0 would paint those regions as a real low value. Numeric strings are
 * accepted because some query layers return measures as text.
 */
function mapHeatmapValue(raw: unknown): number | null {
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  const coerced = Number(raw);
  return Number.isFinite(coerced) ? coerced : null;
}

/**
 * Restrict a county map to the selected state so the color domain — and so the
 * legend — is derived from that state's counties only. A nationwide dataset
 * would otherwise scale the palette to the global maximum and leave the
 * selected state's counties bunched at the pale end.
 */
function filterToState(
  data: readonly HeatmapDataPoint[],
  level: HeatmapLevel,
  stateFips: string | undefined,
): HeatmapDataPoint[] {
  if (level !== 'counties' || !stateFips) {
    return [...data];
  }
  const paddedState = stateFips.trim().padStart(2, '0');
  return data.filter((row) => statePrefixFromGeoId(row.geoId) === paddedState);
}

/**
 * The authored look, forwarded by name.
 *
 * Each field is passed through rather than defaulted: the renderer documents
 * its own defaults, and restating them here would mean a default change in the
 * renderer silently stops applying to every map the adapter feeds.
 */
function heatmapLook(
  presentation: HeatmapPresentation | undefined,
): HeatmapLook {
  return {
    normalization: presentation?.normalization,
    colorScale: presentation?.colorScale,
    colorScaleConfig: presentation?.colorScaleConfig,
    legendVisible: presentation?.legendVisible,
    legendPosition: presentation?.legendPosition,
    strokeWidth: presentation?.strokeWidth,
    scaleFactor: presentation?.scaleFactor,
    zoomable: presentation?.zoomable,
    projectionConfig: presentation?.projectionConfig,
  };
}

function heatmapProps(
  config: HeatmapAdapterConfig | undefined,
  data: readonly HeatmapDataPoint[],
): HeatmapAdapterProps {
  const level = config?.presentation?.level ?? DEFAULT_LEVEL;
  const stateFips = config?.presentation?.stateFips;
  const { height, fillContainer } = chartPresentationProps(
    config?.presentation,
  );

  return {
    ...heatmapLook(config?.presentation),
    data: filterToState(data, level, stateFips),
    level,
    stateFips,
    height,
    fillContainer,
  };
}

export function heatmapAdapter(
  rows: unknown,
  config: HeatmapAdapterConfig,
): RenderAdapterResult<HeatmapAdapterProps> {
  const validated = validateRowsInput(rows, heatmapProps(config, []));
  if ('props' in validated) {
    return validated;
  }

  const columnError = assertRequiredColumns(validated.rows, [
    config.geoIdKey,
    config.valueKey,
  ]);
  if (columnError) {
    return { props: heatmapProps(config, []), error: columnError };
  }

  return {
    props: heatmapProps(
      config,
      validated.rows.map((row) => {
        const level = config?.presentation?.level ?? DEFAULT_LEVEL;
        return {
          geoId: normalizeGeoId(String(row[config.geoIdKey] ?? ''), level),
          value: mapHeatmapValue(row[config.valueKey]),
        };
      }),
    ),
  };
}
